/** Routes. Task detail opens over any route via ?task=id; /t/:id is the deep link from notifications. */
import { Suspense, lazy } from 'react';
import { Outlet, createRootRoute, createRoute, createRouter } from '@tanstack/react-router';
import { AppShell } from '@/components/shell/AppShell';
import { DialogHost } from '@/components/shell/dialogs';
import { EditorHost } from '@/components/shell/EditorHost';
import { MyTasksView } from '@/views/MyTasksView';
import { TaskDetailHost } from '@/views/TaskDetailHost';
import { QuickAddHost } from '@/views/QuickAddHost';
import { GlobalShortcuts } from '@/views/Shortcuts';

const ListView = lazy(() => import('@/views/ListView').then((m) => ({ default: m.ListView })));
const InboxView = lazy(() => import('@/views/InboxView'));
const BoardView = lazy(() => import('@/views/BoardView'));
const CalendarView = lazy(() => import('@/views/CalendarView'));
const SettingsView = lazy(() => import('@/settings/SettingsView'));
const MoreView = lazy(() => import('@/views/MoreView'));
const TrashView = lazy(() => import('@/views/TrashView'));
const SetupGuide = lazy(() => import('@/views/SetupGuide'));
const CommandPalette = lazy(() => import('@/views/CommandPalette'));

const Loading = () => <div className="p-8 text-ink-3">Loading…</div>;

const rootRoute = createRootRoute({
  validateSearch: (s: Record<string, unknown>): { task?: string } =>
    typeof s.task === 'string' ? { task: s.task } : {},
  component: () => (
    <AppShell>
      <Suspense fallback={<Loading />}>
        <Outlet />
      </Suspense>
      <TaskDetailHost />
      <QuickAddHost />
      <DialogHost />
      <EditorHost />
      <Suspense fallback={null}>
        <CommandPalette />
      </Suspense>
      <GlobalShortcuts />
    </AppShell>
  ),
});

const r = <P extends string>(path: P) => ({ getParentRoute: () => rootRoute, path }) as const;

const routeTree = rootRoute.addChildren([
  createRoute({ ...r('/'), component: MyTasksView }),
  createRoute({ ...r('/t/$taskId'), component: MyTasksView }),
  createRoute({ ...r('/inbox'), component: () => <InboxView /> }),
  createRoute({ ...r('/l/$listId'), component: () => <ListView /> }),
  createRoute({ ...r('/l/$listId/board'), component: () => <BoardView /> }),
  createRoute({ ...r('/l/$listId/calendar'), component: () => <CalendarView scope="list" /> }),
  createRoute({ ...r('/calendar'), component: () => <CalendarView scope="all" /> }),
  createRoute({ ...r('/more'), component: () => <MoreView /> }),
  createRoute({ ...r('/spaces'), component: () => <MoreView /> }),
  createRoute({ ...r('/trash'), component: () => <TrashView /> }),
  createRoute({ ...r('/settings'), component: () => <SettingsView /> }),
  createRoute({ ...r('/settings/$section'), component: () => <SettingsView /> }),
  createRoute({ ...r('/setup/$guide'), component: () => <SetupGuide /> }),
]);

export const router = createRouter({ routeTree, defaultPreload: false, scrollRestoration: true });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
